/* eslint-disable no-param-reassign */
import { get, camelCase } from 'lodash';
import { DEFAULT_ERROR, APP_CONFIG_CATEGORIES } from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import {
  convertObjToCamelCase,
  convertObjToSnakeCase,
  retrieveDataMapper,
  retrieveListOrObject,
  retrieveObjectMapper,
  retrieveSortForMS,
} from '../utils';
import { transformConfigByCategory } from '../utils/ms-utils/ms-phase-3/SchoolConfigUtils';
import {
  schoolService,
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
} from '../utils/msEndpointConstants';

const fileName = 'src/services/SchoolConfigService.js';
const { SCHOOL } = MS_PHASE_3_ENDPOINTS;
const { SCHOOL: SCHOOL_P2B } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;

const updateSchoolConfig = (req = {}) => {
  return schoolService()
    .put(
      ...SCHOOL.updateSchoolConfig(get(req, 'ID'), {
        key: get(req, 'Config.key'),
        value: get(req, 'Config.value'),
        fk_school: get(req, 'IDSchool'),
      })
    )
    .then(res => retrieveObjectMapper(res?.data, ['ID:id', 'key', 'value']));
};

const getConfigByCategory = async (schoolId, category = '') =>
  schoolService()
    .post(...SCHOOL.getConfigByCategory(schoolId, category))
    .then(r => transformConfigByCategory(get(r, 'data', {})));

export const getNextLevelsByLevelIds = async (levelIds = []) => {
  if (!levelIds?.length) return {};
  return schoolService()
    .get(...SCHOOL.getNextLevelsByLevelIds(levelIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getConfigByCategories = (categories = [], filter = {}) => {
  if (!categories?.length) return {};
  return schoolService()
    .post(SCHOOL.getConfigByCategories, {
      categories: Object.values(categories),
      filter,
    })
    .then(r => get(r, 'data.categories', {}))
    .catch(() => ({}));
};

export const msGetConfigByCategory = async (dispatch, reqData) => {
  try {
    const searchCategory = get(reqData, 'searchCategory', '');
    const fkSchool = get(reqData, 'filter.fkSchool');
    const findAllConfigByCategory = await getConfigByCategory(
      fkSchool,
      searchCategory
    );
    const data = {
      data: {
        findAllConfigByCategory,
        success: true,
      },
    };

    if (searchCategory === 'work_place_staff') {
      let workplaceArr = null;
      workplaceArr = get(data, 'data.findAllConfigByCategory.data');
      data.data.findAllConfigByCategory.fullData = [...workplaceArr];
      if (workplaceArr?.length > 0) {
        workplaceArr = workplaceArr.filter(item => item.display);
        data.data.findAllConfigByCategory.data = workplaceArr;
      }
    }
    if (searchCategory === 'cancel_registration_reason') {
      let reasonArray = null;
      reasonArray = get(data, 'data.findAllConfigByCategory.data');
      if (reasonArray?.length > 0) {
        const otherIndex = reasonArray.findIndex(
          item => item.label === 'others'
        );
        reasonArray.push(reasonArray.splice(otherIndex, 1)[0]);
        data.data.findAllConfigByCategory.data = reasonArray;
      }
    }

    reqData?.dispatchCategoryFetch?.(dispatch, false, {
      data: data.data,
      type: searchCategory,
    });

    return data;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetConfigByCategory',
        fileName,
      },
      'error'
    );
    reqData?.dispatchCategoryFetch?.(
      dispatch,
      false,
      { data: null, type: get(reqData, 'searchCategory') },
      get(error, 'message', DEFAULT_ERROR)
    );
    return {
      data: {},
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAppConfig = async (dispatch, reqData) => {
  try {
    const fkSchool = get(reqData, 'fkSchool');

    const categoriesResponse = await getConfigByCategories(
      Object.values(APP_CONFIG_CATEGORIES),
      { schools_ids: [fkSchool] }
    );

    const configByCategories = Object.entries(APP_CONFIG_CATEGORIES).reduce(
      (acc, [key, value]) => {
        acc[key] = transformConfigByCategory(
          get(categoriesResponse, value, {})
        );
        return acc;
      },
      {}
    );

    const selfEmployedOccupationGroupConfigs = (
      get(configByCategories, 'selfEmployedOccupationGroupConfig.data') || []
    ).map(c => c.label);

    const occupationResponse = await getConfigByCategories(
      selfEmployedOccupationGroupConfigs,
      {
        schools_ids: [fkSchool],
      }
    );

    const result = {
      data: {
        ...configByCategories,
        selfEmployedConfig: {
          data: Object.entries(occupationResponse).reduce(
            (acc, [key, value]) => {
              acc[camelCase(key)] = transformConfigByCategory(value);
              return acc;
            },
            {}
          ),
        },
      },
      success: true,
    };
    reqData?.dispatchFetchAppConfig?.(dispatch, false, result.data);
    return result.data;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAppConfig',
        fileName,
      },
      'error'
    );
    reqData?.dispatchFetchAppConfig?.(
      dispatch,
      false,
      get(error, 'message', DEFAULT_ERROR)
    );
    return {
      data: {},
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateSchoolConfig = async (_, reqData) => {
  try {
    const responses = await Promise.all(
      (reqData || []).map(req => updateSchoolConfig(req))
    );
    return {
      data: {
        ...responses.reduce((obj, res, index) => {
          obj[`updateSchoolConfig${index + 1}`] = res;
          return obj;
        }, {}),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateSchoolConfig',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msGetDebtWriteOffReasons = async () => {
  try {
    const schoolSvc = schoolService();
    const resp = await schoolSvc.get(...SCHOOL.getDebtWriteOffReasonCodes());

    const data = resp?.data?.data?.map(item =>
      retrieveObjectMapper(item, ['ID:id', 'description', 'label'])
    );

    return {
      success: true,
      data: {
        getDebtWriteOffReasonCodes: {
          data: data || [],
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetDebtWriteOffReasons',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllBusinessEntities = async () => {
  try {
    const schoolSvc = schoolService();
    const resp = await schoolSvc.get(...SCHOOL.getAllBusinessEntities());
    const data =
      resp?.data?.data?.map(item =>
        retrieveObjectMapper(item, ['ID:id', 'name', 'type:type_string'])
      ) || [];

    return {
      success: true,
      data: {
        findAllBusinessEntity: {
          data,
          totalCount: resp?.data?.totalCount || 0,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllBusinessEntities',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const findAllLevelsConfig = async (_, reqData) => {
  try {
    const schoolSvc = schoolService();
    const resp = await schoolSvc
      .post(
        ...SCHOOL.getAllLevelConfigs(
          {
            school_id: [{ eq: get(reqData, 'filter.fkSchool') }],
          },
          {
            ...retrieveSortForMS(get(reqData, 'pagination.sort', [])),
          }
        )
      )
      .then(r => ({
        totalCount: get(r, 'data.totalCount', 0),
        data: get(r, 'data.data', []),
      }))
      .catch(() => ({
        totalCount: 0,
        data: [],
      }));

    const levelIds = get(resp, 'data', []).map(d => get(d, 'id'));
    const nextLevels = await getNextLevelsByLevelIds(levelIds);

    return {
      success: true,
      data: {
        findAllLevelsConfig: {
          data: (resp?.data || [])?.map(d => ({
            ...retrieveObjectMapper(d, ['ID:id', 'label', 'code']),
            nextLevel: retrieveDataMapper(nextLevels, d?.id, [
              'ID:id',
              'label',
            ]),
          })),
          totalCount: resp?.totalCount || 0,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'findAllLevelsConfig',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      data: {
        findAllLevelsConfig: {
          data: [],
          totalCount: 0,
        },
      },
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getSchoolById = id => {
  if (!id) return {};
  return schoolService()
    .get(SCHOOL.getSchoolById(id))
    .then(r => get(r, 'data', {}))
    .catch(() => ({}));
};

export const getCodeByIds = ids => {
  if (!ids) return {};
  return schoolService()
    .post(...SCHOOL.getCodesByCodeId(ids))
    .then(r => get(r, 'data', {}))
    .catch(() => ({}));
};

export const getLevelByIds = (levelIds = []) => {
  if (!levelIds?.length) return [];
  return schoolService()
    .post(...SCHOOL_P2B.GET_LEVEL_BY_IDS(levelIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getCodeByCodeIds = (codeIds = []) => {
  if (!codeIds?.length) return [];
  return schoolService()
    .post(...SCHOOL.getCodesByCodeId(codeIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msGetHHIIncomeLevelOptions = async () => {
  try {
    const res = await schoolService().get(SCHOOL.getHHIIncomeLevelOptions);
    return {
      data: {
        getHHIIncomeLevelOptions: (
          get(res, 'data.hhi_income_level_options') || []
        ).map(r => convertObjToCamelCase(r)),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetHHIIncomeLevelOptions',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchManagementLevels = async (_, reqData) => {
  try {
    const res = await schoolService().get(
      `${SCHOOL.getLevelsForSchool}/${get(reqData, 'IDSchool')}`
    );

    return {
      data: {
        getLevelsForSchool: {
          data: (get(res, 'data.data') || []).map(item =>
            convertObjToCamelCase(item)
          ),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchManagementLevels',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msInsertOrUpdateLevel = async (_, reqData) => {
  try {
    const { IDSchool, levelData, levelID = 0 } = reqData;
    const payload = {
      fkSchool: IDSchool,
      ...levelData,
    };

    if (levelID) {
      payload.levelID = levelID;
    }

    const snakeCasePayload = convertObjToSnakeCase(payload, [
      'fk_school:fkSchool',
      'level_id:levelID',
    ]);

    const res = await schoolService().post(
      ...SCHOOL.insertOrUpdateLevel(snakeCasePayload)
    );

    return {
      data: {
        insertOrUpdateLevelForSchool: convertObjToCamelCase(
          get(res, 'data.data', {})
        ),
      },
      success: true,
    };
  } catch (error) {
    const fullErrMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msInsertOrUpdateLevel',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};

export const msRemoveLevel = async (_, reqData) => {
  try {
    const { levelID } = reqData;
    await schoolService().delete(SCHOOL.deleteLevel(levelID));

    return {
      data: {
        removeLevelForSchool: {},
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveLevel',
        fileName,
      },
      'error'
    );
    const fullErrMsg = get(error, 'response.data.error', DEFAULT_ERROR);

    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};

export const msGetECDACombinedWorkingStatus = async () => {
  try {
    const resp = await schoolService().get(
      SCHOOL.getECDACombinedWorkingStatus()
    );

    return {
      data: {
        getECDACombinedWorkingStatus: convertObjToCamelCase(resp?.data || {}),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetECDACombinedWorkingStatus',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetSchoolLogoUploadURL = async () => {
  try {
    const resp = await Promise.resolve();
    return {
      data: {
        getSchoolLogoUploadURL: {
          Url: get(resp, 'data.url'),
          Key: get(resp, 'data.key'),
        },
      },
      success: true,
    };
  } catch (error) {
    const fullErrMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetSchoolLogoUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};

export const msSendAppDownloadLink = async () => {
  try {
    const resp = await Promise.resolve();
    return {
      data: {
        sendDownloadMobileAppLink: get(resp, 'data.url', false),
      },
      success: true,
    };
  } catch (error) {
    const fullErrMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSendAppDownloadLink',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};
